import {
  IndicatorFactType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelFilter,
} from '@/api/rest/resources/types/fact';
import { Plot } from '@/api/rest/resources/types/plot';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { GRAPH_TYPE, GraphTypeValue } from '@/components/Charts/hooks/useChartType';
import { NCCardAnalysisType, useNCCardContext } from '@/components/NCCard/NCCard';
import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';
import { usePlotReportForPlots } from '@/pages/shared/hooks/usePlotReportForPlot';
import { formatFactLabelsForRequest, hasLabel } from '@/pages/shared/hooks/utils';

import { ItemizedPlot } from './usePlotsForItemizedGraph';

type UseGraphFactsToPlotsMapProps = {
  aggregationPlots: ItemizedPlot[];
  indicator: GraphTileType;
  graphType: GraphTypeValue;
  plotGroupId?: string;
  labels?: PlotReportFactLabelFilter;
};

export const useIndicatorFactsMap = (): Partial<
  Record<GraphTypeValue, Partial<Record<GraphTileType, Partial<Record<NCCardAnalysisType, IndicatorFactType | null>>>>>
> => {
  const aboveGroundCarbonFacts = useAboveGroundCarbonFacts();

  return {
    [GRAPH_TYPE.LINE]: {
      [GraphTileType.historic_below_ground_carbon]: {
        total: IndicatorName.soil_carbon_historic_total_graph,
        average: IndicatorName.soil_carbon_historic_per_ha_graph,
      },
      [GraphTileType.historic_above_ground_carbon]: {
        total: aboveGroundCarbonFacts?.total.graph,
        average: aboveGroundCarbonFacts?.perHa.graph,
      },
      [GraphTileType.historic_water_soil_moisture]: {
        total: IndicatorName.water_and_soil_moisture_historic_total_graph,
        average: IndicatorName.water_and_soil_moisture_historic_per_ha_graph,
      },
      [GraphTileType.protected_on_farm_habitat]: {
        total: IndicatorName.biodiversity_buffer_zone_pct_historic_graph,
        average: null,
      },
      [GraphTileType.social_life_expectancy]: {
        total: IndicatorName.social_life_expectancy_historic_graph,
        average: null,
      },
      [GraphTileType.ndvi]: {
        total: IndicatorName.vegetation_condition_ndvi_historic_graph,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness]: {
        total: IndicatorName.habitat_intactness_total_historic_graph,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness_threats_breakdown]: {
        total: IndicatorName.threat_breakdown_total_historic_graph,
        average: null,
      },
      [GraphTileType.historic_deforestation]: {
        total: IndicatorName.deforestation_total_historic_graph,
        average: null,
      },
      [GraphTileType.canopy_cover]: {
        total: IndicatorName.canopy_cover_pct_graph,
        average: null,
      },
      [GraphTileType.canopy_height]: {
        total: IndicatorName.canopy_height_graph,
        average: null,
      },
    },
    [GRAPH_TYPE.BAR]: {
      [GraphTileType.historic_below_ground_carbon]: {
        total: IndicatorName.permanent_soil_carbon_total,
        average: IndicatorName.permanent_soil_carbon_per_ha,
      },
      [GraphTileType.historic_above_ground_carbon]: {
        total: IndicatorName.above_ground_carbon_total,
        average: IndicatorName.above_ground_carbon_per_ha,
      },
      [GraphTileType.historic_water_soil_moisture]: {
        total: IndicatorName.water_fc_total,
        average: IndicatorName.water_fc_per_ha,
      },
      [GraphTileType.protected_on_farm_habitat]: {
        total: IndicatorName.biodiversity_buffer_zone_pct,
        average: null,
      },
      [GraphTileType.social_life_expectancy]: {
        total: IndicatorName.social_life_expectancy,
        average: null,
      },
      [GraphTileType.ndvi]: {
        total: IndicatorName.vegetation_condition_ndvi,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness]: {
        total: IndicatorName.habitat_intactness_total,
        average: null,
      },
      [GraphTileType.historic_habitat_intactness_threats_breakdown]: {
        total: IndicatorName.threat_breakdown_total,
        average: null,
      },
      [GraphTileType.historic_deforestation]: {
        total: IndicatorName.deforestation_total,
        average: null,
      },
      [GraphTileType.canopy_cover]: {
        total: IndicatorName.canopy_cover_pct,
        average: null,
      },
      [GraphTileType.canopy_height]: {
        total: IndicatorName.canopy_height,
        average: null,
      },
    },
  };
};

export type GraphFactsToPlotsMap = {
  plot: ItemizedPlot;
  facts: PlotReportFactElement[];
}[];

export const useGraphFactsToPlotsMap = ({
  aggregationPlots,
  indicator,
  graphType,
  plotGroupId,
  labels,
}: UseGraphFactsToPlotsMapProps): GraphFactsToPlotsMap => {
  const factType = useFactTypeForItemizedGraph({ indicator, graphType });

  const plotReportsData = usePlotReportForPlots(aggregationPlots as Plot[], {
    plot_group_id: plotGroupId ?? undefined,
    fact_names: factType ?? undefined,
    ...formatFactLabelsForRequest(labels),
  });

  return aggregationPlots.map((plot, index) => {
    const facts = plotReportsData[`${index}`]?.data?.facts ?? [];

    let filteredFacts = facts as PlotReportFactElement[];

    if (!labels || !factType || filteredFacts.length === 0) {
      return {
        plot,
        facts: filteredFacts,
      };
    }

    // We need to filter the facts by their labels because the backend is ignoring the label filter
    const isGraphFact = factType.includes('graph');

    filteredFacts = isGraphFact
      ? filteredFacts.map((f) => {
          if (!Array.isArray(f.value)) {
            return f;
          }

          const filteredValues = f.value.filter((value) => hasLabel(labels, value.labels ?? []));

          return {
            ...f,
            value: filteredValues,
          };
        })
      : filteredFacts.filter((f) => hasLabel(labels, f.labels ?? []));

    return {
      plot,
      facts: filteredFacts,
    };
  });
};

export const useFactTypeForItemizedGraph = ({
  indicator,
  graphType,
}: {
  indicator: GraphTileType;
  graphType: GraphTypeValue;
}): IndicatorFactType | null => {
  const { analysisType } = useNCCardContext();
  const indicatorFactsMap = useIndicatorFactsMap();
  return indicatorFactsMap[`${graphType}`]?.[`${indicator}`]?.[`${analysisType}`] ?? null;
};
